import { apiFetch, apiFetchBlob, apiUploadFormData } from "@/lib/api";

export type ReimbursementKind = "expense" | "per_diem" | "mileage" | "wellness";
export type ReimbursementStatus = "draft" | "submitted" | "approved" | "rejected" | "reimbursed";

export type ExpenseCategory = {
  key: string;
  group: "Travel" | "Trade Show" | "General";
  label: string;
  hint: string | null;
};

export type ReceiptParse = {
  merchant: string | null;
  incurredOn: string | null;
  amount: number | null;
  currency: string | null;
  suggestedCategoryKey: string | null;
  description: string | null;
  confidence: "high" | "medium" | "low";
};

export type ReimbursementReceipt = {
  id: string;
  lineId: string | null;
  filename: string;
  mimeType: string;
  byteSize: number;
  createdAt: string;
};

export type ReimbursementLine = {
  id: string;
  lineNumber: number;
  categoryKey: string | null;
  categoryLabel: string | null;
  merchant: string | null;
  description: string | null;
  amount: number | null;
  incurredOn: string | null;
};

export type Reimbursement = {
  id: string;
  submitterUserId: string;
  submitterDisplayName: string | null;
  submitterEmail: string | null;
  kind: ReimbursementKind;
  status: ReimbursementStatus;
  startOn: string | null;
  endOn: string | null;
  incurredOn: string | null;
  amountRequested: number | null;
  explanation: string | null;
  destination: string | null;
  purpose: string | null;
  departedBefore9am: boolean | null;
  returnedAfter5pm: boolean | null;
  companyPaidMealsLodging: string | null;
  advanceAmount: number | null;
  miles: number | null;
  accountingNote: string | null;
  amountPaid: number | null;
  amountApproved: number | null;
  paidOn: string | null;
  reviewedAt: string | null;
  reimbursedAt: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines: ReimbursementLine[];
  receipts: ReimbursementReceipt[];
  wellnessAllotment: WellnessAllotment | null;
};

export type WellnessSchedule = "full_time" | "part_time";

export type WellnessAllotment = {
  year: number;
  schedule: WellnessSchedule | null;
  annualUsd: number;
  usedUsd: number;
  pendingUsd: number;
  remainingUsd: number;
  thisRequestUsd: number;
  overByUsd: number;
};

export function describeWellnessAllotment(
  allotment: WellnessAllotment,
  thisAmount: number,
): { summary: string; warning: string | null; suggestedApproveUsd: number } {
  const requested = Number.isFinite(thisAmount) ? Math.max(0, Math.round(thisAmount * 100) / 100) : 0;
  const remaining = allotment.remainingUsd;
  const overBy = Math.max(0, Math.round((requested - remaining) * 100) / 100);
  const suggestedApproveUsd = Math.round(Math.min(requested, remaining) * 100) / 100;
  const scheduleLabel = allotment.schedule === "part_time"
    ? "Part-time allotment is $400 per calendar year."
    : allotment.schedule === "full_time"
      ? "Full-time allotment is $800 per calendar year."
      : "Schedule is not set, so this uses the full-time $800 cap until Accounting marks the person part-time.";
  const summary = `${allotment.year} wellness: ${formatReimbursementMoney(allotment.usedUsd)} of ${formatReimbursementMoney(allotment.annualUsd)} used. ${formatReimbursementMoney(remaining)} left. ${scheduleLabel}`;
  let warning: string | null = null;
  if (overBy > 0) {
    warning = remaining > 0
      ? `This request is ${formatReimbursementMoney(overBy)} over the ${allotment.year} allotment. Accounting can approve ${formatReimbursementMoney(remaining)}.`
      : `The ${allotment.year} wellness allotment is already used. Accounting can approve this only as an exception.`;
  } else if (allotment.usedUsd + allotment.pendingUsd + requested > allotment.annualUsd) {
    const pendingOver = Math.round((allotment.usedUsd + allotment.pendingUsd + requested - allotment.annualUsd) * 100) / 100;
    warning = `Other wellness requests still waiting would put ${allotment.year} ${formatReimbursementMoney(pendingOver)} over the allotment if they are paid in full.`;
  }
  return { summary, warning, suggestedApproveUsd };
}

export type ReimbursementDraftPatch = {
  startOn?: string | null;
  endOn?: string | null;
  incurredOn?: string | null;
  amountRequested?: number | null;
  explanation?: string | null;
  destination?: string | null;
  purpose?: string | null;
  departedBefore9am?: boolean | null;
  returnedAfter5pm?: boolean | null;
  companyPaidMealsLodging?: string | null;
  advanceAmount?: number | null;
  miles?: number | null;
};

export function reimbursementKindLabel(kind: ReimbursementKind): string {
  if (kind === "expense") return "Expense";
  if (kind === "per_diem") return "Per diem";
  if (kind === "mileage") return "Mileage";
  return "Wellness";
}

export function reimbursementStatusLabel(status: ReimbursementStatus): string {
  if (status === "draft") return "Draft";
  if (status === "submitted") return "Submitted";
  if (status === "approved") return "Approved";
  if (status === "rejected") return "Needs changes";
  return "Reimbursed";
}

export function formatReimbursementMoney(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return "";
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export function reimbursementSubmitterLabel(row: Pick<Reimbursement, "submitterDisplayName" | "submitterEmail">): string {
  const name = row.submitterDisplayName?.trim() ?? "";
  if (name !== "") return name;
  const email = row.submitterEmail?.trim() ?? "";
  return email !== "" ? email : "Staff member";
}

export function reimbursementEditable(status: ReimbursementStatus): boolean {
  return status === "draft" || status === "rejected";
}

export async function fetchExpenseCategories(scope: "personal" | "accounting"): Promise<ExpenseCategory[]> {
  const path = scope === "accounting"
    ? "/api/accounting/reimbursements/categories"
    : "/api/personal/reimbursements/categories";
  const data = await apiFetch<{ categories: ExpenseCategory[] }>(path);
  return data.categories;
}

export async function fetchMyReimbursements(): Promise<Reimbursement[]> {
  const data = await apiFetch<{ rows: Reimbursement[] }>("/api/personal/reimbursements");
  return data.rows;
}

export async function fetchMyReimbursement(id: string): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(`/api/personal/reimbursements/${encodeURIComponent(id)}`);
  return data.reimbursement;
}

export async function createReimbursementDraft(kind: ReimbursementKind): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>("/api/personal/reimbursements", {
    method: "POST",
    body: { kind },
  });
  return data.reimbursement;
}

export async function saveReimbursementDraft(id: string, patch: ReimbursementDraftPatch): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(`/api/personal/reimbursements/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
  return data.reimbursement;
}

export async function submitMyReimbursement(id: string): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(
    `/api/personal/reimbursements/${encodeURIComponent(id)}/submit`,
    { method: "POST" },
  );
  return data.reimbursement;
}

export async function deleteMyReimbursement(id: string): Promise<void> {
  await apiFetch(`/api/personal/reimbursements/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function parseReceiptFile(file: File): Promise<ReceiptParse> {
  const formData = new FormData();
  formData.set("file", file);
  const data = await apiUploadFormData<{ parse: ReceiptParse }>("/api/personal/reimbursements/parse-receipt", formData);
  return data.parse;
}

export async function uploadReimbursementReceipt(
  id: string,
  file: File,
  fields: {
    categoryKey?: string;
    merchant?: string;
    description?: string;
    amount?: string;
    incurredOn?: string;
    aiParse?: ReceiptParse | null;
  },
): Promise<Reimbursement> {
  const formData = new FormData();
  formData.set("file", file);
  if (fields.categoryKey) formData.set("categoryKey", fields.categoryKey);
  if (fields.merchant) formData.set("merchant", fields.merchant);
  if (fields.description) formData.set("description", fields.description);
  if (fields.amount) formData.set("amount", fields.amount);
  if (fields.incurredOn) formData.set("incurredOn", fields.incurredOn);
  if (fields.aiParse) formData.set("aiParse", JSON.stringify(fields.aiParse));
  const data = await apiUploadFormData<{ reimbursement: Reimbursement }>(
    `/api/personal/reimbursements/${encodeURIComponent(id)}/receipts`,
    formData,
  );
  return data.reimbursement;
}

export async function deleteMyReceipt(id: string, receiptId: string): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(
    `/api/personal/reimbursements/${encodeURIComponent(id)}/receipts/${encodeURIComponent(receiptId)}`,
    { method: "DELETE" },
  );
  return data.reimbursement;
}

export function receiptContentPath(scope: "personal" | "accounting", id: string, receiptId: string): string {
  const root = scope === "accounting" ? "/api/accounting/reimbursements" : "/api/personal/reimbursements";
  return `${root}/${encodeURIComponent(id)}/receipts/${encodeURIComponent(receiptId)}`;
}

export async function fetchReceiptObjectUrl(path: string): Promise<string> {
  const result = await apiFetchBlob(path);
  return URL.createObjectURL(result.blob);
}

export async function fetchAccountingReimbursements(status: string): Promise<Reimbursement[]> {
  const query = status === "" ? "" : `?status=${encodeURIComponent(status)}`;
  const data = await apiFetch<{ rows: Reimbursement[] }>(`/api/accounting/reimbursements${query}`);
  return data.rows;
}

export async function fetchAccountingReimbursement(id: string): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(
    `/api/accounting/reimbursements/${encodeURIComponent(id)}`,
  );
  return data.reimbursement;
}

export async function approveAccountingReimbursement(id: string, amountApproved?: number): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(
    `/api/accounting/reimbursements/${encodeURIComponent(id)}/approve`,
    { method: "POST", body: amountApproved == null ? {} : { amountApproved } },
  );
  return data.reimbursement;
}

export async function setAccountingWellnessSchedule(
  id: string,
  schedule: WellnessSchedule,
): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(
    `/api/accounting/reimbursements/${encodeURIComponent(id)}/wellness-schedule`,
    { method: "PATCH", body: { schedule } },
  );
  return data.reimbursement;
}

export async function rejectAccountingReimbursement(id: string, note: string): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(
    `/api/accounting/reimbursements/${encodeURIComponent(id)}/reject`,
    { method: "POST", body: { note } },
  );
  return data.reimbursement;
}

export async function markAccountingReimbursementPaid(
  id: string,
  input: { amountPaid: number; paidOn: string; note?: string },
): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(
    `/api/accounting/reimbursements/${encodeURIComponent(id)}/reimburse`,
    { method: "POST", body: input },
  );
  return data.reimbursement;
}

export async function updateAccountingLineCategory(
  id: string,
  lineId: string,
  categoryKey: string,
): Promise<Reimbursement> {
  const data = await apiFetch<{ reimbursement: Reimbursement }>(
    `/api/accounting/reimbursements/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
    { method: "PATCH", body: { categoryKey } },
  );
  return data.reimbursement;
}
